import { Chip } from "@gigpro/ui/Chip";
import { Skeleton } from "@gigpro/ui/Skeleton";
import type { Certificate, ProShiftAvailabilitySlice, TimeBlock } from "@sidegigllc/api/client";
import { useHasJobsAccess } from "domains/hire/hooks/useHasJobsAccess";
import type React from "react";
import { getWorkTypeOrder, isHighlightedWorkType } from "../../utils/workType";
import { CertificationBadge, isSupportedCertification } from "../CertificationBadge";
import { PROfileAvailability } from "../PROfileAvailability";

export type PreferenceTypeId = "work-type" | "positions" | "days" | "shifts" | "hours";

export type ProPreferencesByType = {
  type: {
    label: string;
    id: PreferenceTypeId;
  };
  preferences: Array<{ label: string; id: number | null | undefined }>;
};

type ProPreferencesProps = {
  preferencesByTypes: Array<ProPreferencesByType>;
};

type PROfileAboutMinimalProps = {
  bio: string;
  gigproCertifications?: Array<Certificate>;
};

type PROfileAboutProps = {
  data: PROfileAboutMinimalProps & {
    preferencesByTypes: Array<ProPreferencesByType>;
    availability?: ReadonlyArray<ProShiftAvailabilitySlice>;
    timeBlocks?: ReadonlyArray<TimeBlock>;
  };
};

export const PROfilePreferences: React.FC<ProPreferencesProps> = ({ preferencesByTypes }) => {
  const hasJobsEnabled = useHasJobsAccess();

  if (preferencesByTypes.length === 0) return null;

  return (
    <>
      {preferencesByTypes.map(({ type, preferences }) => {
        const sortedPreferences =
          hasJobsEnabled && type.id === "work-type"
            ? preferences.toSorted((a, b) => getWorkTypeOrder(a.label) - getWorkTypeOrder(b.label))
            : preferences;

        return (
          <div key={type.id} className="my-2 flex gap-1">
            <p data-testid={`preference_type_${type.id}`} className="@sm/profile:text-large">
              {type.label}
            </p>
            <div className="flex flex-wrap gap-1">
              {sortedPreferences.map((preference) => (
                <Chip
                  // `days_of_week` has no ids, and seven nulls would collide as keys.
                  key={preference.id ?? preference.label}
                  data-testid={`preference_${type.label}_${preference.label}`}
                  className={
                    hasJobsEnabled && type.id === "work-type" && isHighlightedWorkType(preference.label)
                      ? "bg-brand text-white"
                      : undefined
                  }
                >
                  {preference.label}
                </Chip>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
};

export const PROfileAboutMinimal: React.FC<PROfileAboutMinimalProps> = ({ bio, gigproCertifications }) => {
  const certificationBadges =
    gigproCertifications
      ?.map(({ id, name, skill_id }) =>
        isSupportedCertification(name) ? (
          <CertificationBadge key={id} certification={name} isSkillCertification={!!skill_id} />
        ) : null,
      )
      ?.filter(Boolean) ?? [];

  return (
    <>
      <p className="@sm/profile:text-large">{bio}</p>
      {certificationBadges.length > 0 && (
        <>
          <h3 className="my-3 font-semibold @sm/profile:text-large">Qwick Certifications</h3>
          <div className="flex flex-wrap gap-1">{certificationBadges}</div>
        </>
      )}
    </>
  );
};

export const PROfileAbout: React.FC<PROfileAboutProps> = ({
  data: { bio, preferencesByTypes, gigproCertifications, availability = [], timeBlocks = [] },
}) => (
  <>
    <h2 className="text-large font-semibold @sm/profile:text-extraLarge">About</h2>
    <PROfileAboutMinimal bio={bio} gigproCertifications={gigproCertifications} />
    <h3 className="my-3 font-semibold @sm/profile:text-large">Preferences</h3>
    <PROfilePreferences preferencesByTypes={preferencesByTypes} />
    <PROfileAvailability slices={availability} timeBlocks={timeBlocks} />
  </>
);

export const PROfileAboutSkeleton = () => (
  <>
    <h2 className="text-large font-semibold @sm/profile:text-extraLarge">About</h2>
    <Skeleton className="w-48 text-normal @sm/profile:text-large" />
    <Skeleton className="w-56 text-normal @sm/profile:text-large" />
    <h3 className="my-3 font-semibold @sm/profile:text-large">Preferences</h3>
    <Skeleton variant="rounded" className="mr-1 inline-block h-5 w-26" />
    <Skeleton variant="rounded" className="mr-1 inline-block h-5 w-32" />
    <Skeleton variant="rounded" className="mr-1 inline-block h-5 w-20" />
    <Skeleton variant="rounded" className="mr-1 inline-block h-5 w-16" />
    <Skeleton variant="rounded" className="mr-1 inline-block h-5 w-26" />
  </>
);
